/**
 * ApplySuggestion Extension for Copilot MessageBlock
 *
 * This extension adds Copilot-specific functionality to the shared MessageBlock:
 * - ApplySuggestionButton for code blocks with `language-apply:*` class
 * - ApplyAllSuggestionsButton in the footer
 * - Custom tool badge rendering (instead of accordion)
 */
import { Hammer } from "lucide-react"

import type { MessageBlockExtension } from "@f2e/ui/components/Chat"

import ApplySuggestionButton from "@/components/Copilot/applySuggestion/ApplySuggestionButton"
import ApplyAllSuggestionsButton from "@/components/Copilot/applySuggestion/ApplyAllSuggestionsButton"
import ApplySuggestionDiff from "@/components/Copilot/applySuggestion/ApplySuggestionDiff"
import { parseApplyLanguageClass } from "@/components/Copilot/applySuggestion/applySuggestionHandler"

/**
 * Derived data computed once per render.
 */
interface ApplyDerived {
  /** All apply blocks found in the message content */
  applyBlocks: Array<{ key: string; value: string }>
  applyTargetPath?: string
}

/**
 * Parse apply blocks from processed content.
 * Finds all code blocks with `language-apply:*` and extracts key/value pairs.
 */
export function parseApplyBlocks(content: string): ApplyDerived["applyBlocks"] {
  const blocks: ApplyDerived["applyBlocks"] = []

  // Match apply blocks using quadruple-fence format.
  // Format: ````apply:fieldKey\nvalue\n````
  const codeBlockRegex = /````apply:([^\s\n]+)\n([\s\S]*?)````/g
  let match

  while ((match = codeBlockRegex.exec(content)) !== null) {
    const key = match[1]
    // Keep the original content as much as possible (do NOT trim),
    // only remove a single trailing newline (matches old behavior).
    const value = match[2].replace(/\r?\n$/, "")
    if (key) blocks.push({ key, value })
  }

  return blocks
}

/**
 * Copilot ApplySuggestion extension for MessageBlock.
 *
 * Features:
 * - Derives apply blocks from content
 * - Adds ApplySuggestionButton to code blocks with apply:* language
 * - Transforms code block metadata for apply blocks (hide language, use markdown syntax)
 * - Renders tool tags as simple badges instead of accordions
 * - Shows ApplyAllSuggestionsButton in footer when there are apply blocks
 */
export const applySuggestionExtension: MessageBlockExtension<ApplyDerived> = {
  id: "copilot-apply-suggestion",

  /**
   * Derive apply blocks from the processed content.
   */
  derive: (ctx) => ({
    applyBlocks: parseApplyBlocks(ctx.processedContent),
    applyTargetPath: ctx.message.meta?.applyTargetPath,
  }),

  /**
   * Code block extensions for apply suggestion functionality.
   */
  codeBlock: {
    renderContent: ({ className, content }) => {
      const applyKey = parseApplyLanguageClass(className)
      if (!applyKey) return null

      return <ApplySuggestionDiff applyKey={applyKey} content={content} />
    },

    /**
     * Render ApplySuggestionButton for code blocks with apply:* language.
     */
    renderActions: ({ className, content, derived }) => {
      const applyKey = parseApplyLanguageClass(className)
      if (!applyKey) return null

      return (
        <ApplySuggestionButton
          applyKey={applyKey}
          content={content}
          applyTargetPath={derived.applyTargetPath}
        />
      )
    },

    /**
     * Transform metadata for apply code blocks:
     * - Hide the language display (empty string)
     * - Use markdown for syntax highlighting
     */
    transformMeta: ({ className }) => {
      const applyKey = parseApplyLanguageClass(className)
      if (!applyKey) return {}

      return {
        displayLanguage: "",
        syntaxLanguage: "markdown",
      }
    },
  },

  /**
   * Render tool tags as simple badges instead of accordions.
   */
  tool: {
    render: ({ children, step }) => {
      // If this tool tag matches a real reasoning step, let the default renderer
      // show the accordion + ReasoningStepRenderer.
      if (step) return null

      // Otherwise (unknown tool tag), keep the lightweight badge rendering.
      return (
        <div className="my-1 w-fit">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] tracking-wide uppercase bg-transparent border border-dashed border-border text-foreground/80 align-middle">
            <Hammer className="w-3 h-3" />
            {children}
          </span>
        </div>
      )
    },
  },

  /**
   * Render ApplyAllSuggestionsButton in footer when there are apply blocks.
   */
  footer: {
    render: ({ derived }) => {
      if (derived.applyBlocks.length === 0) return null

      return (
        <div className="flex justify-end mb-2">
          <ApplyAllSuggestionsButton
            applyBlocks={derived.applyBlocks}
            applyTargetPath={derived.applyTargetPath}
          />
        </div>
      )
    },
    position: "prepend", // ApplyAll appears above ActionBar
  },
}

export default applySuggestionExtension
